/**
 * MetricKeyCombobox Component
 *
 * Searchable, grouped picker for metric API keys.
 * Built on the existing Popover primitive (no new dependency) with a native
 * input for search and a keyboard-navigable list of grouped metrics.
 *
 * Shared between:
 *  - Custom Metric widget formula variables (WidgetConfigPanel)
 *  - Goal widget metric-key picker (parallel UX fix)
 *
 * Backward compatibility: if the saved `value` does not exist in any group,
 * it is displayed in the trigger flagged as "Unknown" with a tooltip — the
 * user is not silently downgraded to an empty selection.
 */
import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { Search, ChevronDown, AlertCircle, Check } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent, cn } from "@provenleap/ui";

export interface MetricKeyComboboxGroup {
  /** Stable group identifier (e.g. platform key like "google-ads") */
  platform: string;
  /** Friendly group label (e.g. "Google Ads") */
  label: string;
  /** Available metric keys in this group */
  metrics: string[];
}

export interface MetricKeyComboboxProps {
  /** Currently selected metric key, or empty string for "not selected" */
  value: string;
  /** Called with the newly selected metric key */
  onChange: (metricKey: string) => void;
  /** Grouped metric options (typically derived from connected integrations) */
  groups: MetricKeyComboboxGroup[];
  /**
   * Optional map of metric-key → friendly label. When absent, the key is
   * humanized (camelCase / snake_case / kebab-case → Title Case).
   */
  labelForKey?: (metricKey: string) => string;
  /**
   * Optional map of metric-key → last-fetched preview value (already
   * formatted for display, e.g. "$1,234"). Rendered next to the option
   * when present.
   */
  previewForKey?: (metricKey: string) => string | undefined;
  /** Placeholder shown when nothing is selected */
  placeholder?: string;
  /** data-testid root; sub-elements append suffixes */
  testId?: string;
  /** When true, the trigger renders compact for inline use (e.g. variable rows) */
  compact?: boolean;
  /** Accessible label for the trigger button */
  ariaLabel?: string;
}

/** Humanize a metric key for display when no friendly label is provided. */
export function humanizeMetricKey(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

export function MetricKeyCombobox({
  value,
  onChange,
  groups,
  labelForKey,
  previewForKey,
  placeholder = "Select metric...",
  testId = "metric-key-combobox",
  compact = false,
  ariaLabel,
}: MetricKeyComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const resolveLabel = useCallback(
    (key: string) => (labelForKey ? labelForKey(key) : humanizeMetricKey(key)),
    [labelForKey],
  );

  // Detect whether the current `value` is known in any group.
  const isKnown = useMemo(() => {
    if (!value) return true; // empty == "not selected", not unknown
    for (const g of groups) {
      if (g.metrics.includes(value)) return true;
    }
    return false;
  }, [value, groups]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        metrics: g.metrics.filter((k) => {
          const label = resolveLabel(k).toLowerCase();
          return label.includes(q) || k.toLowerCase().includes(q);
        }),
      }))
      .filter((g) => g.metrics.length > 0);
  }, [groups, search, resolveLabel]);

  const totalMatches = useMemo(
    () => filteredGroups.reduce((sum, g) => sum + g.metrics.length, 0),
    [filteredGroups],
  );

  // Focus search when popover opens.
  useEffect(() => {
    if (open) {
      // Defer to next tick so Popover content is mounted.
      const t = setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    setSearch("");
    return undefined;
  }, [open]);

  const handleSelect = useCallback(
    (key: string) => {
      onChange(key);
      setOpen(false);
    },
    [onChange],
  );

  const triggerLabel = value
    ? isKnown
      ? resolveLabel(value)
      : `${value} (Unknown)`
    : placeholder;

  return (
    <div className="relative w-full">
      {/* Hidden input mirrors the selected value so tests/forms can read it via getByTestId(testId).toHaveValue(...) */}
      <input
        type="hidden"
        value={value}
        readOnly
        data-testid={testId}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={ariaLabel ?? placeholder}
            aria-haspopup="listbox"
            aria-expanded={open}
            data-testid={`${testId}-trigger`}
            className={cn(
              "w-full flex items-center justify-between gap-1 rounded border border-border bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring",
              compact ? "px-1.5 py-1 text-xs" : "px-2 py-1.5 text-sm",
              !value && "text-muted-foreground",
            )}
          >
            <span className="flex items-center gap-1 truncate">
              {value && !isKnown && (
                <AlertCircle
                  className="h-3 w-3 shrink-0 text-amber-500"
                  aria-label="Unknown metric key"
                  data-testid={`${testId}-unknown`}
                />
              )}
              <span className="truncate">{triggerLabel}</span>
            </span>
            <ChevronDown className="h-3 w-3 shrink-0 opacity-60" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-72 p-0"
          data-testid={`${testId}-popover`}
        >
          <div className="flex items-center gap-1.5 border-b border-border px-2 py-1.5">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search metrics..."
              className="flex-1 bg-transparent text-xs focus:outline-none placeholder:text-muted-foreground"
              data-testid={`${testId}-search`}
            />
          </div>
          <div
            role="listbox"
            className="max-h-72 overflow-y-auto py-1"
            data-testid={`${testId}-list`}
          >
            {totalMatches === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                No metrics match &ldquo;{search}&rdquo;
              </div>
            ) : (
              filteredGroups.map((group) => (
                <div key={group.platform} className="mb-1 last:mb-0">
                  <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {group.label}
                  </div>
                  {group.metrics.map((key) => {
                    const preview = previewForKey?.(key);
                    const isSelected = key === value;
                    return (
                      <button
                        key={`${group.platform}:${key}`}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => handleSelect(key)}
                        data-testid={`${testId}-option-${key}`}
                        className={cn(
                          "flex w-full items-center gap-1.5 px-2 py-1 text-left text-xs hover:bg-accent",
                          isSelected && "bg-accent/50",
                        )}
                      >
                        <Check
                          className={cn(
                            "h-3 w-3 shrink-0",
                            isSelected ? "opacity-100 text-primary" : "opacity-0",
                          )}
                        />
                        <span className="flex-1 truncate">{resolveLabel(key)}</span>
                        {preview !== undefined && (
                          <span
                            className="ml-2 shrink-0 font-mono text-[10px] text-muted-foreground"
                            data-testid={`${testId}-option-${key}-preview`}
                          >
                            {preview}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
